import { TZDate } from '@date-fns/tz';

/**
 * UTC timestamp → US Eastern calendar date (YYYY-MM-DD). SOT for all game-date anchoring
 * (projection lookups, MLB schedule officialDate, ROI snapshot keys). Invalid input falls
 * back to the ISO date prefix, else ''.
 */
export function utcToEasternDate(input: string | Date): string {
  if (!input) return '';
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) {
    return typeof input === 'string' ? input.split('T')[0] || '' : '';
  }
  return date.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

/**
 * Date → UTC calendar date (YYYY-MM-DD), i.e. the `.toISOString().split('T')[0]` idiom in
 * one place. ⚠ This is the *UTC* day, NOT ET — for game-date anchoring use utcToEasternDate.
 * Centralized so the UTC-vs-ET intent is explicit at every call site (this app is ET-sensitive).
 */
export function toUtcDateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

/** UTC timestamp → ET wall-clock time with suffix ("7:05 PM ET"). All user-facing
 *  game times render in ET. '' on missing/invalid input. */
export function formatEtTime(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return '';
  return `${date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  })} ET`;
}

/**
 * Current ET time as minutes-since-midnight. Uses formatToParts with hourCycle: 'h23'
 * so we get deterministic "00".."23" regardless of locale quirks (some locales emit "24").
 * Intl handles DST, so comparisons against wall-clock HH:MM window bounds Just Work.
 */
export function getEtMinutesSinceMidnight(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const hour = Number(parts.find(p => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find(p => p.type === 'minute')?.value ?? 0);
  return hour * 60 + minute;
}

/**
 * [start, end) of a US-Eastern calendar day (YYYY-MM-DD) as UTC instants: its midnight to the next day's. Midnight ET is
 * never ambiguous (the clocks change at 2 a.m.), so a DST day is simply 23 or 25 hours long.
 */
export function etSlateWindow(slateDate: string): { start: string; end: string } {
  const [y, m, d] = slateDate.split('-').map(Number);
  const midnight = (day: number) => new Date(new TZDate(y, m - 1, day, 'America/New_York').getTime()).toISOString();
  return { start: midnight(d), end: midnight(d + 1) };
}
